import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { IRole } from "@/hooks/session/useRoles";
import { Business } from "@mui/icons-material";
import {
  FormControl,
  ListItemText,
  ListItemIcon,
  MenuItem,
  Select,
  styled,
  SelectChangeEvent,
  Skeleton,
  CircularProgress,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

// Estilo customizado para o Select no header
const StyledSelect = styled(FormControl)(({ theme }) => ({
  margin: theme.spacing(0, 2),
  minWidth: 200,
  "& .MuiOutlinedInput-root": {
    color: "white",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.5)",
    },
    "&:hover fieldset": {
      borderColor: "white",
    },
    "&.Mui-focused fieldset": {
      borderColor: "white",
    },
  },
  "& .MuiSelect-icon": {
    color: "white",
  },
  "& .MuiInputBase-input": {
    padding: "8px 14px",
  },
}));

const HeaderOrganizationSelect: React.FC = () => {
  const { organization, organizationList, loading } = useOrganizationContext();
  const { roles, setAccess } = useSessionContext();

  function getRoles(data: any) {
    return [
      ...(data?.isManager ? ["manager"] : []),
      ...(data?.isPractitioner ? ["practitioner"] : []),
      ...(data?.isOwner ? ["owner"] : []),
      ...(data?.isInvited ? ["invited"] : []),
    ];
  }

  const activeOrganizations =
    roles?.filter((role) => {
      const org = organizationList?.find(
        (e: fhir5.BundleEntry<fhir5.Organization>) =>
          e?.resource?.id === role?.organization?.reference?.split("/")[1] &&
          e?.resource?.active === true
      );
      return org !== undefined && !role?.isInvited;
    }) || [];

  const navigate = useNavigate();

  const handleChange = (data?: any) => {
    const auxOrganizationId = data.organization.reference
      ?.split("/")[1]
      ?.split("-")[1];

    setAccess({
      type: "professional",
      organizationId: auxOrganizationId,
      roles: getRoles(data),
    });

    localStorage.setItem("organizationId", auxOrganizationId);
    navigate("/");

    //verificar contratos da clinica selecionada
    //verificar se é profissional alem de owner e manager
    // redirecionar para tela de perfil com cancelamento aberto se tiver recusado o termo
  };

  return (
    <StyledSelect>
      {loading ? (
        <Skeleton variant="rectangular" width="100%" height={40} />
      ) : (
        <>
          <Select
            value={organization?.id?.split("-")[1] || ""}
            onChange={handleChange}
            sx={{ color: "white" }}
            renderValue={(selected) => {
              if (!selected) return "Selecione uma clínica";
              const role = roles?.find((r) => {
                return r.organization?.reference?.split("-")[1] === selected;
              });

              const auxOrganization = organizationList?.find(
                (e: fhir5.BundleEntry<fhir5.Organization>) =>
                  e?.resource?.id ===
                  role?.organization?.reference?.split("/")[1]
              );

              return auxOrganization?.resource?.alias?.[0] !== "null"
                ? auxOrganization?.resource?.alias?.[0]
                : auxOrganization?.resource?.name ||
                    role?.organization?.display ||
                    "-";
            }}
            startAdornment={<Business sx={{ mr: 1, color: "white" }} />}
          >
            {activeOrganizations.length > 0 ? (
              activeOrganizations.map((el) => {
                const auxOrganization = organizationList?.find(
                  (e: fhir5.BundleEntry<fhir5.Organization>) =>
                    e?.resource?.id ===
                    el?.organization?.reference?.split("/")[1]
                );

                return auxOrganization?.resource?.active ? (
                  <MenuItem
                    value={
                      el.organization?.reference?.split("/")[1]?.split("-")[1]
                    }
                    onClick={() => handleChange(el)}
                    key={`org-select-${el.organization?.display}`}
                  >
                    <ListItemIcon>
                      <Business />
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        auxOrganization?.resource?.alias?.[0] !== "null"
                          ? auxOrganization?.resource?.alias?.[0]
                          : auxOrganization?.resource?.name ||
                            el?.organization?.display ||
                            "-"
                      }
                    />
                  </MenuItem>
                ) : null;
              })
            ) : (
              <MenuItem value="" disabled>
                Nenhuma organização ativa encontrada
              </MenuItem>
            )}
          </Select>
        </>
      )}
    </StyledSelect>
  );
};

export { HeaderOrganizationSelect };
